import React, { useEffect, useState } from "react";
import { Button, Table, Modal, Pagination, Input, Space } from "antd";
import type { InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import "antd/dist/antd.css";
import "react-datepicker/dist/react-datepicker.css";
import * as S from "./styles";
import api from "../../service/api";
import { registerLocale } from "react-datepicker";
import { ptBR } from "date-fns/locale";
import ListaCategoria from '../../components/Categoria/ListaCategoria/ListaCategoria';

registerLocale("pt-BR", ptBR);

interface DataItem {
  empresa: string;
  cnpj: string;
  banco: string;
  agencia: string;
  contaCorrente: string;
  data: string;
  descricao: string;
  valor: string;
  id?: string;
  categoria?: string;
}

interface Props {
  currentUser: any;
}

const TablePortalAdministradoras: React.FC<Props> = ({ currentUser }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataItem[]>([]);
  const [bancos, setBancos] = useState<string[]>([]);
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [contas, setContas] = useState<string[]>([]);
  const [categorias, setCategorias] = useState<string[]>([]);

  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedBanco, setSelectedBanco] = useState("");
  const [selectedConta, setSelectedConta] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [tipoLancamento, setTipoLancamento] = useState<"" | "entrada" | "saida">("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;
  const [isOpen, setIsOpen] = useState(false);
  const [totalSum, setTotalSum] = useState(0);
  const [grupoDoCliente, setGrupoDoCliente] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [valorFiltro, setValorFiltro] = useState("");
  const searchInputRef = React.useRef<InputRef | null>(null);

  const history = useHistory();
  const [startDate, endDate] = dateRange;

  const formatDateOnly = (date: Date | null | undefined): string | null => {
    return date ? date.toISOString().split("T")[0] : null;
  };

  const formatarData = (data: string | null | undefined): string => {
    if (!data) return "";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  function generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  useEffect(() => {
    const cliente = localStorage.getItem("cliente");
    if (cliente) {
      try {
        const clienteObj = JSON.parse(cliente);
        if (clienteObj && clienteObj.nomeDoGrupo) {
          setGrupoDoCliente(clienteObj.nomeDoGrupo);
        }
      } catch (e) {
        console.error("Erro ao fazer parse do cliente de localStorage:", e);
        toast.error("Erro ao carregar dados do cliente.");
      }
    }
  }, []);

  const fetchData = async (
    page = 1,
    empresa = "",
    banco = "",
    conta = "",
    categoria = "",
    dataInicial: Date | null = null,
    dataFinal: Date | null = null,
    tipo: "" | "entrada" | "saida" = "",
    valor = ""
  ) => {
    if (!grupoDoCliente) {
      setIsLoading(false);
      return;
    }

    const startIndex = (page - 1) * pageSize;

    setIsLoading(true);
    try {
      const response = await api.get("/Extratos/listarExtratos", {
        params: {
          nomeDoGrupo: grupoDoCliente,
          startIndex,
          empresa,
          banco,
          conta,
          extrato: categoria,
          dataInicial: formatDateOnly(dataInicial),
          dataFinal: formatDateOnly(dataFinal),
          tipoLancamento: tipo || null,
          valor: valor || null,
        },
      });

      if (response.data.success) {
        const dataWithUniqueKeys = response.data.result.map((item: DataItem) => ({
          ...item,
          id: generateUUID(),
        }));
        setData(dataWithUniqueKeys);
        setBancos(response.data.bancos || []);
        setEmpresas(response.data.empresas || []);
        setContas(response.data.contas || []);
        const categoriasFiltradas = (response.data.categorias || []).filter((c: string) => c);
        setCategorias(categoriasFiltradas);
        setCurrentPage(page);
        setTotalItems(response.data.totalCount || 0);
        setTotalSum(response.data.somaValorTotal || 0);
      } else {
        toast.error("Erro: " + response.data.message);
      }
    } catch {
      toast.error("Erro ao buscar dados.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaginationChange = (page: number) => {
    fetchData(page, selectedCompany, selectedBanco, selectedConta, selectedCategoria, startDate, endDate, tipoLancamento, valorFiltro);
  };

  const fetchExtratos = async () => {
    if (!grupoDoCliente) {
      toast.warn("Nome do grupo do cliente não disponível para baixar extrato.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        nomeDoGrupo: grupoDoCliente.trim(),
        dataInicial: formatDateOnly(startDate),
        dataFinal: formatDateOnly(endDate),
      };

      const response = await api.get("/Extratos/baixarExtratosCSV", {
        params: payload,
        responseType: "blob",
      });

      const responseName = await api.get("/Extratos/pegarNomeArquivoCSV", { params: payload });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", responseName.data);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Arquivo baixado com sucesso!");
    } catch (error: any) {
      toast.error("Erro ao baixar extrato: " + (error.message || "Erro desconhecido."));
    } finally {
      setIsLoading(false);
    }
  };

  const formatCNPJ = (cnpj: string) =>
    cnpj ? cnpj.replace(/\D/g, "").replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "";

  const getColumnSearchProps = (): any => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder="Buscar Valor"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">
            Buscar
          </Button>
          <Button onClick={() => { clearFilters(); confirm(); }} size="small">
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ fontSize: 18, color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    },
  });

  useEffect(() => {
    if (grupoDoCliente) {
      fetchData(1, selectedCompany, selectedBanco, selectedConta, selectedCategoria, startDate, endDate, tipoLancamento, valorFiltro);
    }
  }, [grupoDoCliente]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(1, selectedCompany, selectedBanco, selectedConta, selectedCategoria, startDate, endDate, tipoLancamento, valorFiltro);
    } else if (!startDate && !endDate) {
      fetchData(1, selectedCompany, selectedBanco, selectedConta, selectedCategoria, null, null, tipoLancamento, valorFiltro);
    }
  }, [selectedCompany, selectedBanco, selectedConta, selectedCategoria, startDate, endDate, tipoLancamento, valorFiltro]);

  const columns = [
    { title: "EMPRESA", dataIndex: "empresa", key: "empresa", width: 100, sorter: false, sortDirections: [] },
    {
      title: "CNPJ",
      dataIndex: "cnpj",
      key: "cnpj",
      width: 80,
      sorter: false,
      sortDirections: [],
      render: (cnpj: string) => <span style={{ whiteSpace: 'nowrap' }}>{formatCNPJ(cnpj)}</span>,
    },
    { title: "BANCO", dataIndex: "banco", key: "banco", width: 150, sorter: false, sortDirections: [] },
    {
      title: "AGÊNCIA",
      dataIndex: "agencia",
      key: "agencia",
      width: 60,
      sorter: false,
      sortDirections: [],
      render: (agencia: string) => agencia ? agencia.substring(0, 4) : '',
    },
    {
      title: "CONTA",
      dataIndex: "contaCorrente",
      key: "contaCorrente",
      width: 130,
      sorter: false,
      sortDirections: [],
      render: (contaCorrente: string) => {
        const paddedValue = (contaCorrente || '').padEnd(10, ' ');
        return paddedValue.substring(0, 10);
      },
    },
    {
      title: "DATA",
      dataIndex: "data",
      key: "data",
      width: 80,
      sorter: false,
      sortDirections: [],
    },
    {
      title: "DESCRIÇÃO",
      dataIndex: "descricao",
      key: "descricao",
      width: 250,
      sorter: false,
      sortDirections: [],
      render: (text: string) => {
        const breakText = (str: string, limit: number) => {
          const regex = new RegExp(`(.{1,${limit}})(\\s|$)`, 'g');
          return str.replace(regex, '$1\n').trim();
        };
        const formattedText = breakText(text, 57);
        return <div style={{ whiteSpace: 'pre-wrap' }}>{formattedText}</div>;
      },
    },
    { title: "CATEGORIA", dataIndex: "categoria", key: "categoria", width: 100, sorter: false, sortDirections: [] },
    {
      title: "VALOR",
      dataIndex: "valor",
      key: "valor",
      width: 100,
      sorter: false,
      sortDirections: [],
      ...getColumnSearchProps(),
      render: (value?: string) => {
        const numericValue = parseFloat((value || "0").replace(",", "."));
        const isNegative = numericValue < 0;
        const formatted = numericValue.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        });
        return (
          <div style={{ color: isNegative ? "red" : "black", textAlign: "right" }}>
            {formatted}
          </div>
        );
      },
    },
  ];

  function openExtratos() {
    const cliente = localStorage.getItem("cliente");

    if (cliente) {
      const clienteObj = JSON.parse(cliente);
      const id = clienteObj?.id;
      if (id) {
        history.replace({
          pathname: "/ListaContas",
          state: { id },
        });
      }
    }
  }

  const handleModalCloseAndUpdate = () => {
    setIsModalVisible(false);
    fetchData(1, selectedCompany, selectedBanco, selectedConta, selectedCategoria, startDate, endDate, tipoLancamento, valorFiltro);
  };

  const handleOpenCategoriasModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <S.Container>
      <S.RoundedModalStyle />

      <S.DivTable>
        <S.Tools>
          <S.LeftContentWrapper>
            <S.SearchContainer>
              <S.MenuContainer>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Selecionar Empresa</option>
                  {empresas.map((empresa) => (
                    <option key={empresa} value={empresa}>
                      {empresa}
                    </option>
                  ))}
                </select>
              </S.MenuContainer>

              <S.MenuContainer>
                <select
                  value={selectedBanco}
                  onChange={(e) => setSelectedBanco(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Selecionar Banco</option>
                  {bancos.map((banco) => (
                    <option key={banco} value={banco}>
                      {banco}
                    </option>
                  ))}
                </select>
              </S.MenuContainer>

              <S.MenuContainer>
                <select
                  value={selectedConta}
                  onChange={(e) => setSelectedConta(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Selecionar Conta</option>
                  {contas.map((conta) => (
                    <option key={conta} value={conta}>
                      {conta}
                    </option>
                  ))}
                </select>
              </S.MenuContainer>

              <S.MenuContainer>
                <select
                  value={selectedCategoria}
                  onChange={(e) => setSelectedCategoria(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Selecionar Categoria</option>
                  <option value="All-categorias">Todas Categorias</option>
                  <option value="All-null">Sem Categorias</option>
                  {categorias.map((categoria) => (
                    <option key={categoria} value={categoria}>
                      {categoria}
                    </option>
                  ))}
                </select>
              </S.MenuContainer>

              <S.MenuContainer>
                <ReactDatePicker
                  selectsRange
                  startDate={dateRange[0] ?? undefined}
                  endDate={dateRange[1] ?? undefined}
                  onChange={(update) => {
                    setDateRange(update as [Date | null, Date | null]);

                    if ((update as [Date | null, Date | null])[0] && (update as [Date | null, Date | null])[1]) {
                      setIsOpen(false);
                    }
                  }}
                  isClearable
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  customInput={<S.DatePickerInput />}
                  placeholderText="Selecione um período"
                  open={isOpen}
                  onClickOutside={() => setIsOpen(false)}
                  onInputClick={() => setIsOpen(true)}
                />
              </S.MenuContainer>

              <S.CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={tipoLancamento === "entrada"}
                    onChange={() => setTipoLancamento(prev => prev === "entrada" ? "" : "entrada")}
                    disabled={isLoading}
                  />
                  Entrada
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={tipoLancamento === "saida"}
                    onChange={() => setTipoLancamento(prev => prev === "saida" ? "" : "saida")}
                    disabled={isLoading}
                  />
                  Saída
                </label>
              </S.CheckboxGroup>
            </S.SearchContainer>

            <S.ButtonGroup>
              <Button
                onClick={() => openExtratos()}
                type="primary"
                style={{
                  borderRadius: "5px",
                }}
              >
                Contas e Extratos
              </Button>

              <Button
                onClick={() => fetchExtratos()}
                type="primary"
                style={{
                  borderRadius: "4px",
                  backgroundColor: "#FAFAFA",
                  borderColor: "#FAFAFA",
                  color: "black",
                }}
              >
                Baixar Extrato (.csv)
              </Button>
            </S.ButtonGroup>
          </S.LeftContentWrapper>

          <S.RightAlignedButtons>
            <Button
              onClick={handleOpenCategoriasModal}
              type="primary"
              style={{
                borderRadius: "4px",
                backgroundColor: "#FAFAFA",
                borderColor: "#FAFAFA",
                color: "black",
                marginTop: "5px"
              }}
            >
              Categorias do Extrato
            </Button>
          </S.RightAlignedButtons>
        </S.Tools>

        <Table
          columns={columns}
          dataSource={data}
          loading={isLoading}
          rowKey={(record) => record.id!}
          pagination={false}
          onChange={(_pagination, filters: any) => {
            const raw = (filters?.valor?.[0] ?? "") as string;
            const cleaned = raw ? raw.replace(/[R$\s.%]/g, "").replace(",", ".") : "";
            setValorFiltro(cleaned);
            setCurrentPage(1);
          }}
        />

        {/* ✅ O contêiner com flexbox e a margem para espaçamento */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', marginBottom: '16px' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalItems}
            showQuickJumper
            showSizeChanger={false}
            onChange={handlePaginationChange}
          />
          <div>
            SOMA VALOR TOTAL: <strong>{totalSum.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
          </div>
        </div>

        <Modal
          open={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={null}
          width="92vw"
          style={{ maxWidth: 1800, top: 20 }}
          closable={false}
        >
          <ListaCategoria currentUser={currentUser} onClose={handleModalCloseAndUpdate} />
        </Modal>
      </S.DivTable>
    </S.Container>
  );
};

export default TablePortalAdministradoras;